import { useEffect } from 'react';

import { Box, Flex } from '@chakra-ui/react';

import { SplitProgressBar } from 'uiToolkitV1/ProgressBar';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

interface SplitProgressGraphProps {
  graphData: Record<string, number>;
}

// Default color palette for dynamic keys
const DEFAULT_COLORS = [
  { barColor: '#ACBAFF', textColor: '#7651BC' },
  { barColor: '#FFDE88', textColor: '#8C6A00' },
  { barColor: '#FD9B9B', textColor: '#C62C02' },
  { barColor: '#B1E39F', textColor: '#44812F' },
  { barColor: '#FFB6FC', textColor: '#7D1779' },
  { barColor: '#A0E7E5', textColor: '#0B6E6C' },
  { barColor: '#FFAEBC', textColor: '#B30E2F' },
  { barColor: '#B5EAD7', textColor: '#2D7A5E' },
];

interface ProgressBarItem {
  label: string;
  weight: number;
  color: string;
  textColor: string;
}

const transformDataForProgressBar = (data: Record<string, number>): ProgressBarItem[] => {
  if (!data || Object.keys(data).length === 0) return [];

  return Object.entries(data).map(([key, value], index) => {
    const colorIndex = index % DEFAULT_COLORS.length;
    return {
      label: key,
      weight: value,
      color: DEFAULT_COLORS[colorIndex].barColor,
      textColor: DEFAULT_COLORS[colorIndex].textColor,
    };
  });
};

const SplitProgressGraph = ({ graphData = {} }: SplitProgressGraphProps) => {
  const transformedData = transformDataForProgressBar(graphData ?? {});
  const totalWeight = Object.values(graphData ?? {})?.reduce((acc, value) => acc + value, 0);

  if (transformedData?.length === 0) {
    return (
      <Flex w="100%" h="100%" alignItems="center">
        <TextBodyMedium color="#333333">NA</TextBodyMedium>
      </Flex>
    );
  }

  return (
    <Box className="split-progress-graph" mt="10px" width="200px">
      <SplitProgressBar size="large" data={transformedData} totalWeight={totalWeight} percentageValue={false} />
      <Flex flexDirection="column">
        {transformedData.map((item) => {
          const currentPercentage = Math.round((item.weight / totalWeight) * 100);

          return (
            <Flex key={item.label} w="100%">
              <Flex mr="8px" justifyContent="space-between">
                <TextLabel
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                  color={item.textColor}
                >
                  {item.label}
                </TextLabel>
                <TextLabel
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                  color={item.textColor}
                >
                  : {currentPercentage}%
                </TextLabel>
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
};

export default SplitProgressGraph;
